import type { RootState } from '@/store';

/**
 * Redacted, JSON-serializable snapshot of the Redux store.
 *
 * Allow-list shaped: only the branches and fields enumerated below are emitted.
 * Any branch not listed here — and any new field added to a listed branch — is
 * dropped by default, so a newly introduced secret cannot leak without a
 * deliberate edit to this file. Enforces hard rule #6 (no secrets in Redux
 * reaching a report): auth tokens (sessionToken, ukek, decapsKey, regToken,
 * registrationToken, recoveryToken), OPAQUE/ML-KEM key material, file keys
 * (swarmKey, encryptedFileKeys on the six branches), and user PII (phone, email)
 * are never included.
 */
export interface RedactedState {
  auth: {
    status: RootState['auth']['status'];
    loginStep: RootState['auth']['loginStep'];
    userId: RootState['auth']['userId'];
    userType: RootState['auth']['userType'];
    isSubmitting: RootState['auth']['isSubmitting'];
  };
  user: {
    user: { id: string; name: string; organization: string; department?: string } | null;
    loadingUser: boolean;
    unreadNotificationCount: number;
  };
  organization: {
    messageBrokerMultiaddr: RootState['organization']['messageBrokerMultiaddr'];
    loadingMessageBrokerMultiaddr: RootState['organization']['loadingMessageBrokerMultiaddr'];
    error: RootState['organization']['error'];
  };
  fileKeyFacilitation: {
    status: RootState['fileKeyFacilitation']['status'];
    processedCount: number;
    totalCount: number;
    errors: string[];
    rerunRequested: boolean;
  };
  debug: {
    isOpen: boolean;
    heightPx: number;
    lastRefreshedAt: RootState['debug']['lastRefreshedAt'];
    logLevel: RootState['debug']['logLevel'];
    logNamespaces: string[];
    p2pDiagnostics: RootState['debug']['p2pDiagnostics'];
  };
}

/**
 * Build a redacted, serializable copy of the store for a bug report.
 * Pure — does not read or mutate the live store.
 */
export function redactSensitiveState(state: RootState): RedactedState {
  const { auth, user, organization, fileKeyFacilitation, debug } = state;

  return {
    auth: {
      status: auth.status,
      loginStep: auth.loginStep,
      userId: auth.userId,
      userType: auth.userType,
      isSubmitting: auth.isSubmitting,
    },
    user: {
      user:
        user.user === null
          ? null
          : {
              id: user.user.id,
              name: user.user.name,
              organization: user.user.organization,
              department: user.user.department,
            },
      loadingUser: user.loadingUser,
      unreadNotificationCount: user.notifications.unreadCount,
    },
    organization: {
      messageBrokerMultiaddr: organization.messageBrokerMultiaddr,
      loadingMessageBrokerMultiaddr: organization.loadingMessageBrokerMultiaddr,
      error: organization.error,
    },
    fileKeyFacilitation: {
      status: fileKeyFacilitation.status,
      processedCount: fileKeyFacilitation.processedCount,
      totalCount: fileKeyFacilitation.totalCount,
      errors: fileKeyFacilitation.errors,
      rerunRequested: fileKeyFacilitation.rerunRequested,
    },
    debug: {
      isOpen: debug.isOpen,
      heightPx: debug.heightPx,
      lastRefreshedAt: debug.lastRefreshedAt,
      logLevel: debug.logLevel,
      logNamespaces: debug.logNamespaces,
      p2pDiagnostics: debug.p2pDiagnostics,
    },
  };
}
